import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Copy, Eye, Pencil } from 'lucide-react';
import axios from 'axios';
import RecordHistory from '../nativeitems/RecordHistory';
import RelatedRecords from '../nativeitems/RelatedRecords';
import AllFieldsPanel from './AllFieldsPanel';
import AttachmentsTab from './AttachmentsTab';
import DocumentActions from './DocumentActions';
import { useRecordDetail, fmtQty, fmtMoney, fmtDate, fmtStamp, statusLabel } from './recordUtils';
import '../nativeitems/NativeItems.css';

// Purchase order record (Native, read-only) — tasks #62/#60. Served from the
// synced purchase_orders / purchase_order_lines mirror with the Run PO-cache
// jsonb lines as rate/received enrichment. Related + History run directly on
// purchase_orders (registry + history are enabled for that table).

const PO_STATUS = {
  A: 'Pending Approval', B: 'Pending Receipt', C: 'Rejected',
  D: 'Partially Received', E: 'Pending Billing / Partially Received',
  F: 'Pending Bill', G: 'Fully Billed', H: 'Closed',
  pending: 'Pending', received: 'Received', partial: 'Partially Received',
  billed: 'Billed', closed: 'Closed', cancelled: 'Cancelled',
};

export default function PurchaseOrderRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const { data, loading, error } = useRecordDetail(`/api/native-records/purchase-orders/${encodeURIComponent(id)}`);

  if (loading) return <div className="nit-page"><div className="nit-loading">Loading purchase order…</div></div>;
  if (error) {
    return (
      <div className="nit-page">
        <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>
        <div className="nit-error">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { record, lines, cache, generatedSalesOrder } = data;
  // Enrich mirror lines with cache rate/received where the mirror predates
  // the unit_cost columns.
  const cacheBySku = new Map((cache?.lineItems || []).map((li) => [li.item_sku, li]));
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'lines', label: 'Lines', count: lines.length || (cache?.lineItems?.length ?? 0) },
    { key: 'fields', label: 'All fields' },
    { key: 'related', label: 'Related' },
    { key: 'history', label: 'History' },
    { key: 'attachments', label: 'Attachments' },
  ];

  return (
    <div className="nit-page">
      <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>

      <div className="nit-item-head" style={{ marginTop: 8 }}>
        <code>{record.order_number || `PO (ns ${record.ns_internal_id})`}</code>
        <h1>{record.vendor_name || 'Purchase order'}</h1>
        <span className="nit-tag">{statusLabel(PO_STATUS, record.status)}</span>
        {record.voided && <span className="nit-tag nit-tag-inactive">voided</span>}
        {record.ns_internal_id && <span className="nit-tag">NS {record.ns_internal_id}</span>}
        <span className="nit-readonly"><Eye size={12} /> read-only</span>
        {/* A native manual PO with no NetSuite twin is editable in place — a PO
            posts nothing, so amending it is a data change. Mirror rows and POs
            that already reached NetSuite stay read-only (the reverse-sync is
            create-only, so an edit here would diverge from what the vendor
            got); the server enforces the same rule on PATCH. */}
        {record.native === true && record.source === 'manual' && record.ns_internal_id == null && !record.voided && (
          <Link to={`/native-purchase-orders/${encodeURIComponent(record.id)}/edit`} className="nit-btn" style={{ marginLeft: 'auto', textDecoration: 'none' }}>
            <Pencil size={14} /> Edit
          </Link>
        )}
        <Link to={`/native-purchase-orders/new?copyFrom=${encodeURIComponent(id)}`} className="nit-btn" style={{ marginLeft: record.native === true ? 0 : 'auto', textDecoration: 'none' }}>
          <Copy size={14} /> Make Copy
        </Link>
        {/* Store reorder (CHS/BOS buying from HQ): one click mints the paired
            HQ sales order — cases at actual wholesale for sellables, supplies
            at cost — and it lands in the warehouse pick queue. Idempotent;
            once it exists the button gives way to a link to the SO. */}
        {generatedSalesOrder ? (
          <Link to={`/records/sales-orders/${encodeURIComponent(generatedSalesOrder.id)}`} className="nit-btn" style={{ textDecoration: 'none' }}>
            Sales Order {generatedSalesOrder.number || ''} →
          </Link>
        ) : (
          <button
            className="nit-btn"
            onClick={async () => {
              try {
                const { data } = await axios.post(`/api/native-records/purchase-orders/${encodeURIComponent(id)}/generate-sales-order`);
                window.alert(`${data.message}${data.notes && data.notes.length ? `\n\n${data.notes.join('\n')}` : ''}`);
                window.location.reload();
              } catch (err) {
                window.alert(err.response?.data?.error || err.message);
              }
            }}
          >
            Generate Sales Order
          </button>
        )}
        <button
          className="nit-btn"
          onClick={async () => {
            if (!window.confirm('Void this document? Every open line is closed on the NetSuite record (history is kept).')) return;
            try {
              const { data } = await axios.post(`/api/native-records/purchase-orders/${encodeURIComponent(id)}/void`);
              window.alert(data.note || 'Voided.');
              window.location.reload();
            } catch (err) {
              window.alert(err.response?.data?.error || err.message);
            }
          }}
        >
          Void
        </button>
        <DocumentActions
          recordId={id}
          printDocs={[{ type: 'purchase-order', label: 'Print / Download PDF' }]}
          emailDoc={{ type: 'purchase-order', label: 'Email…' }}
        />
      </div>

      <div className="nit-tabs">
        {tabs.map((t) => (
          <button key={t.key} className={`nit-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
            {t.count != null && <span className="nit-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="nit-grid">
          <div className="nit-card">
            <h3>Order</h3>
            <div className="nit-facts">
              <span>Order date</span><strong>{fmtDate(record.order_date)}</strong>
              <span>Status</span><strong>{statusLabel(PO_STATUS, record.status)}</strong>
              <span>Total</span><strong>{fmtMoney(record.total_amount)}</strong>
              <span>Amount paid</span><strong>{fmtMoney(record.amount_paid)}</strong>
              <span>Amount remaining</span><strong>{fmtMoney(record.amount_remaining)}</strong>
              <span>Terms</span><strong>{record.terms || '—'}</strong>
              <span>Expected delivery</span><strong>{fmtDate(cache?.expectedDeliveryDate)}</strong>
              <span>Memo</span><strong>{record.memo || '—'}</strong>
            </div>
          </div>

          <div className="nit-card">
            <h3>Buyer &amp; receiving</h3>
            <div className="nit-facts">
              <span>Vendor</span><strong>{record.vendor_name || '—'}</strong>
              <span>Buyer</span><strong>{record.buyer_name || '—'}</strong>
              <span>Ship to (warehouse)</span><strong style={{ whiteSpace: 'pre-wrap' }}>{record.ship_address || '—'}</strong>
              <span>Posting period</span><strong>{record.posting_period || '—'}</strong>
            </div>
            {record.buyer_name == null && record.ns_raw == null && (
              <p className="nit-caption">Buyer + receiving warehouse arrive with the full-field capture backfill (migration 20260705).</p>
            )}
          </div>
        </div>
      )}

      {tab === 'lines' && (
        <div className="nit-card">
          <h3>Lines</h3>
          {lines.length === 0 && (cache?.lineItems || []).length === 0 && (
            <div className="nit-empty">No lines in the mirror for this PO.</div>
          )}
          {lines.length > 0 && (
            <div className="nit-table-wrap">
              <table className="nit-table">
                <thead>
                  <tr>
                    <th>#</th><th>Item</th>
                    <th className="r">Ordered</th><th className="r">Billed</th>
                    <th className="r">Unit cost</th><th className="r">Amount</th>
                    <th>Memo</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const cacheLine = cacheBySku.get(l.item_name) || null;
                    const unitCost = l.unit_cost ?? cacheLine?.rate ?? null;
                    return (
                      <tr key={l.id || l.line_number} className={l.is_closed ? 'nit-inactive-row' : ''}>
                        <td className="nit-muted">{l.line_number}</td>
                        <td>{l.item_name || l.item_id}</td>
                        <td className="r">{fmtQty(l.quantity_ordered)}</td>
                        <td className="r nit-muted">{fmtQty(l.quantity_billed)}</td>
                        <td className="r">{fmtMoney(unitCost, 4)}</td>
                        <td className="r">{fmtMoney(l.amount)}</td>
                        <td className="nit-muted">{l.line_memo || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {lines.length === 0 && (cache?.lineItems || []).length > 0 && (
            <div className="nit-table-wrap">
              <table className="nit-table">
                <thead>
                  <tr><th>SKU</th><th className="r">Ordered</th><th className="r">Received</th><th className="r">Rate</th></tr>
                </thead>
                <tbody>
                  {cache.lineItems.map((li, i) => (
                    <tr key={`${li.item_sku}-${i}`}>
                      <td><code>{li.item_sku}</code></td>
                      <td className="r">{fmtQty(li.ordered_quantity)}</td>
                      <td className="r">{fmtQty(li.received_quantity)}</td>
                      <td className="r">{fmtMoney(li.rate, 4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {cache && <p className="nit-caption">PO cache synced {fmtStamp(cache.syncedAt)}.</p>}
        </div>
      )}

      {tab === 'fields' && (
        <AllFieldsPanel
          family="transaction"
          nsRaw={record.ns_raw}
          overrides={{ employee: record.buyer_name }}
        />
      )}

      {/* purchase_orders is registered in packages/related and history-enabled
          in packages/history — both tabs run on this record's own id. */}
      {tab === 'related' && <RelatedRecords recordType="purchase_order" id={record.id} />}
      {tab === 'history' && <RecordHistory table="purchase_orders" rowId={record.id} />}
      {tab === 'attachments' && <AttachmentsTab recordType="purchase_order" recordId={id} />}
    </div>
  );
}
